import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import SelfieCapture from "@/components/SelfieCapture";
import GeoWidget from "@/components/GeoWidget";
import { api, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { EFFECTIVE_STATUS, EFFECTIVE_STATUS_CLS } from "@/constants/roles";
import { LogIn, LogOut as LogOutIcon, CheckCircle2, Clock, Globe, Building2 } from "lucide-react";

function haversineMeters(a, b) {
  if (!a || !b) return null;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export default function EmployeeDashboard() {
  const [now, setNow] = useState(new Date());
  const [pos, setPos] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [offices, setOffices] = useState([]);
  const [todayData, setTodayData] = useState({ attendance: null, is_wfa: false });
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    const [s, t, o, u] = await Promise.all([
      api.get("/settings"),
      api.get("/attendance/today"),
      api.get("/offices"),
      api.get("/auth/me"),
    ]);
    setSettings(s.data);
    setTodayData(t.data);
    setOffices(o.data);
    setMe(u.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!navigator.geolocation) { setGeoError("Browser tidak mendukung geolokasi."); return; }
    const watchId = navigator.geolocation.watchPosition(
      (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setGeoError(null); },
      (err) => setGeoError(err.message || "Gagal mendapatkan lokasi."),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // Determine best matching assigned office
  const assignedOffices = useMemo(() => {
    if (!me || !offices.length) return [];
    return offices.filter((o) => (me.office_ids || []).includes(o.office_id));
  }, [me, offices]);

  const bestOfficeMatch = useMemo(() => {
    if (!pos || !assignedOffices.length) return null;
    let best = null;
    for (const o of assignedOffices) {
      const d = haversineMeters({ lat: pos.lat, lng: pos.lng }, { lat: o.lat, lng: o.lng });
      if (best == null || d < best.distance) best = { office: o, distance: d };
    }
    return best;
  }, [pos, assignedOffices]);

  const isWFA = todayData?.is_wfa;
  const distance = bestOfficeMatch?.distance;
  const inRange = isWFA
    ? true
    : (bestOfficeMatch && distance != null && distance <= bestOfficeMatch.office.radius_meters);

  const dx = pos && bestOfficeMatch ? (pos.lng - bestOfficeMatch.office.lng) * 100000 : 0;
  const dy = pos && bestOfficeMatch ? (bestOfficeMatch.office.lat - pos.lat) * 100000 : 0;

  const att = todayData?.attendance;
  const hasCheckedIn = !!att?.check_in_time;
  const hasCheckedOut = !!att?.check_out_time;

  const submit = async (kind) => {
    if (!selfie) return toast.error("Ambil swafoto terlebih dahulu.");
    if (!pos) return toast.error("Lokasi belum siap.");
    if (!isWFA && !inRange) return toast.error("Anda berada di luar radius kantor.");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/attendance/${kind}`, {
        latitude: pos.lat, longitude: pos.lng, selfie_base64: selfie,
      });
      setTodayData((prev) => ({ ...prev, attendance: data }));
      setSelfie(null);
      toast.success(kind === "check-in" ? "Check-in berhasil." : "Check-out berhasil.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal memproses.");
    } finally {
      setSubmitting(false);
    }
  };

  const canCheckIn = (isWFA || inRange) && !!selfie && !hasCheckedIn && !submitting && !!pos;
  const canCheckOut = (isWFA || inRange) && !!selfie && hasCheckedIn && !hasCheckedOut && !submitting && !!pos;

  return (
    <div data-testid={TESTIDS.empDashboard} className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-tiny">Dashboard Karyawan</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Absensi Hari Ini</h1>
          <p className="text-gray-600 mt-2 text-sm">
            {now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div data-testid={TESTIDS.digitalClock} className="border border-black/10 bg-white px-6 py-4">
          <div className="label-tiny">Waktu Lokal</div>
          <div className="font-display font-black text-4xl tracking-tighter mt-1 tabular-nums">
            {now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
        </div>
      </div>

      {isWFA && (
        <div data-testid={TESTIDS.wfaBanner} className="border border-klein bg-klein/5 p-4 flex items-center gap-3">
          <Globe className="w-5 h-5 text-klein" />
          <div>
            <div className="font-display font-bold text-klein">Mode Work From Anywhere Aktif</div>
            <div className="text-sm text-gray-600">Anda tidak terikat radius kantor hari ini. Cukup ambil swafoto & pastikan GPS aktif.</div>
          </div>
        </div>
      )}

      {!isWFA && assignedOffices.length === 0 && (
        <div className="border border-signal-late bg-red-50 p-4 text-sm text-signal-late">
          Anda belum ditugaskan ke kantor manapun. Hubungi admin untuk penugasan.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <SelfieCapture onCapture={setSelfie} disabled={hasCheckedIn && hasCheckedOut} />
          {isWFA ? (
            <WFACard pos={pos} loading={!pos} />
          ) : (
            <GeoWidget
              userPos={pos ? { lat: pos.lat, lng: pos.lng, dx, dy } : null}
              office={bestOfficeMatch?.office}
              distance={distance}
              inRange={!!inRange}
              loading={!bestOfficeMatch || !pos}
            />
          )}
        </div>

        <div className="space-y-6">
          <div data-testid={TESTIDS.todayCard} className="border border-black/10 bg-white p-6">
            <div className="label-tiny">Status Hari Ini</div>
            <div className="mt-3 space-y-3">
              <Row label="Check-in" icon={<LogIn className="w-4 h-4" />} value={formatTime(att?.check_in_time)} />
              <Row label="Check-out" icon={<LogOutIcon className="w-4 h-4" />} value={formatTime(att?.check_out_time)} />
              {att?.office_name && (
                <Row label="Lokasi" icon={<Building2 className="w-4 h-4" />} value={att.office_name} />
              )}
              <div className="pt-3 border-t border-black/10 flex items-center justify-between">
                <div className="label-tiny">Status Absensi</div>
                <StatusBadge status={att?.status} hasCheckedIn={hasCheckedIn} effective={todayData?.effective_status} />
              </div>
            </div>
          </div>

          <button
            data-testid={TESTIDS.checkInBtn}
            onClick={() => submit("check-in")}
            disabled={!canCheckIn}
            className="w-full bg-klein text-white py-5 font-display font-bold text-xl tracking-tight hover:bg-black transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <span className="inline-flex items-center gap-3"><LogIn className="w-5 h-5" /> CHECK-IN</span>
          </button>
          <button
            data-testid={TESTIDS.checkOutBtn}
            onClick={() => submit("check-out")}
            disabled={!canCheckOut}
            className="w-full border-2 border-black text-black py-5 font-display font-bold text-xl tracking-tight hover:bg-black hover:text-white transition-colors disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <span className="inline-flex items-center gap-3"><LogOutIcon className="w-5 h-5" /> CHECK-OUT</span>
          </button>

          {geoError && <div className="border border-signal-late text-signal-late p-3 text-sm">{geoError}</div>}
        </div>
      </div>

      {settings && (
        <div className="border border-black/10 bg-white p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Info label="Perusahaan" value={settings.company_name} />
          <Info label="Jam Masuk" value={settings.check_in_time} icon={<Clock className="w-4 h-4" />} />
          <Info label="Jam Pulang" value={settings.check_out_time} icon={<Clock className="w-4 h-4" />} />
          <Info label="Kantor Ditugaskan" value={`${assignedOffices.length} lokasi`} icon={<Building2 className="w-4 h-4" />} />
        </div>
      )}
    </div>
  );
}

function WFACard({ pos, loading }) {
  return (
    <div className="border border-black/10 bg-white flex flex-col">
      <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between">
        <div className="label-tiny">Work From Anywhere</div>
        <div className="text-xs text-signal-ok font-semibold">Aktif</div>
      </div>
      <div className="flex-1 min-h-[240px] flex items-center justify-center p-8 text-center bg-[repeating-linear-gradient(45deg,#f5f5f5_0_10px,#ffffff_10px_20px)]">
        <div>
          <Globe className="w-12 h-12 mx-auto text-klein mb-3" />
          <div className="font-display font-bold text-xl">Bebas Lokasi</div>
          <div className="text-sm text-gray-600 mt-2 max-w-xs">Radius kantor dinonaktifkan. Cukup swafoto & GPS aktif untuk verifikasi.</div>
        </div>
      </div>
      {pos?.lat && (
        <div className="px-4 py-2 border-t border-black/10 text-xs font-mono text-gray-600">
          Posisi: {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}
        </div>
      )}
      {loading && <div className="px-4 py-2 border-t border-black/10 text-xs text-gray-500">Menunggu GPS...</div>}
    </div>
  );
}

function Row({ label, value, icon }) {
  return (
    <div className="flex items-center justify-between">
      <div className="inline-flex items-center gap-2 text-sm text-gray-600">{icon}{label}</div>
      <div className="font-display font-bold text-lg tabular-nums">{value}</div>
    </div>
  );
}

function Info({ label, value, icon }) {
  return (
    <div>
      <div className="label-tiny">{label}</div>
      <div className="font-display font-bold text-xl mt-1 inline-flex items-center gap-2">{icon}{value}</div>
    </div>
  );
}

function StatusBadge({ status, hasCheckedIn, effective }) {
  // Prefer effective status when available (covers cuti/izin/sakit/dinas_luar/pulang_cepat/alpha).
  if (effective) {
    const label = EFFECTIVE_STATUS[effective] || effective;
    const cls = EFFECTIVE_STATUS_CLS[effective] || "bg-gray-200";
    return <span data-testid={TESTIDS.statusBadge} className={`px-2 py-1 text-xs font-bold uppercase tracking-widest ${cls}`}>{label}</span>;
  }
  if (!hasCheckedIn) {
    return <span data-testid={TESTIDS.statusBadge} className="px-2 py-1 bg-gray-100 border border-black/10 text-xs font-bold uppercase tracking-widest">Belum</span>;
  }
  if (status === "late") {
    return <span data-testid={TESTIDS.statusBadge} className="px-2 py-1 bg-signal-late text-white text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Terlambat</span>;
  }
  return <span data-testid={TESTIDS.statusBadge} className="px-2 py-1 bg-signal-ok text-white text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Ontime</span>;
}
